import {
  ForecastGameStats,
  ForecastOutcome,
} from '@/modules/calendar/model/forecast-game/forecast-game-state';
import { computeSpecialists } from '@/modules/calendar/model/forecast-game/specialists';

export type BadgeTier = 'bronze' | 'silver' | 'gold';

export interface BadgeDefinition {
  id: string;
  label: string;
  emoji: string;
  description: string;
  // Tiered badges belong to a family and upgrade bronze -> silver -> gold.
  // Badges with no tier/familyId are one-off achievements — there's nothing
  // "better" to earn on top of them, so they're never shown as upgradeable.
  tier?: BadgeTier;
  familyId?: string;
}

export interface BadgeFamily {
  id: string;
  label: string;
}

export const BADGE_FAMILIES: BadgeFamily[] = [
  { id: 'streak', label: 'Win Streak' },
  { id: 'total-wins', label: 'Total Wins' },
  { id: 'accuracy', label: 'Accuracy' },
  { id: 'specialists', label: 'Specialists' },
];

export const BADGES: BadgeDefinition[] = [
  // --- Win Streak: consecutive wins against MNI ---
  {
    id: 'hot-streak-3',
    label: 'Hot Streak',
    emoji: '🔥',
    description: '3 wins in a row against MNI.',
    tier: 'bronze',
    familyId: 'streak',
  },
  {
    id: 'on-fire-5',
    label: 'On Fire',
    emoji: '🔥',
    description: '5 wins in a row against MNI.',
    tier: 'silver',
    familyId: 'streak',
  },
  {
    id: 'iron-will-10',
    label: 'Iron Will',
    emoji: '⚡',
    description: '10 wins in a row against MNI.',
    tier: 'gold',
    familyId: 'streak',
  },

  // --- Total Wins: lifetime win count ---
  {
    id: 'rookie-numbers',
    label: 'Rookie Numbers',
    emoji: '🎲',
    description: '5 total wins against MNI.',
    tier: 'bronze',
    familyId: 'total-wins',
  },
  {
    id: 'crystal-ball',
    label: 'Crystal Ball',
    emoji: '🔮',
    description: '10 total wins against MNI.',
    tier: 'silver',
    familyId: 'total-wins',
  },
  {
    id: 'half-century',
    label: 'Half Century',
    emoji: '👑',
    description: '25 total wins against MNI.',
    tier: 'gold',
    familyId: 'total-wins',
  },

  // --- Accuracy: win rate, gated by a minimum sample size so it can't be
  // farmed with 1-2 lucky forecasts ---
  {
    id: 'sharp-shooter',
    label: 'Sharp Shooter',
    emoji: '🎯',
    description: '55%+ win rate over at least 5 resolved forecasts.',
    tier: 'bronze',
    familyId: 'accuracy',
  },
  {
    id: 'marksman',
    label: 'Marksman',
    emoji: '🏹',
    description: '65%+ win rate over at least 10 resolved forecasts.',
    tier: 'silver',
    familyId: 'accuracy',
  },
  {
    id: 'oracle',
    label: 'Oracle',
    emoji: '🧙',
    description: '75%+ win rate over at least 20 resolved forecasts.',
    tier: 'gold',
    familyId: 'accuracy',
  },

  // --- Specialists: how many indicators you've earned an edge in (reuses
  // the same "Specialist in..." logic already shown on Overview) ---
  {
    id: 'niche-expert',
    label: 'Niche Expert',
    emoji: '📌',
    description: "You're a specialist in 1 indicator.",
    tier: 'bronze',
    familyId: 'specialists',
  },
  {
    id: 'well-rounded',
    label: 'Well Rounded',
    emoji: '🧩',
    description: "You're a specialist in 3 indicators.",
    tier: 'silver',
    familyId: 'specialists',
  },
  {
    id: 'renaissance-forecaster',
    label: 'Renaissance Forecaster',
    emoji: '🎓',
    description: "You're a specialist in 5 indicators.",
    tier: 'gold',
    familyId: 'specialists',
  },

  // --- Unique achievements: one-off, no upgrade path ---
  {
    id: 'first-blood',
    label: 'First Blood',
    emoji: '🎯',
    description: 'Resolved your first forecast.',
  },
  {
    id: 'beat-the-machine',
    label: 'Beat the Machine',
    emoji: '🤖',
    description: 'First time closer to the actual value than MNI consensus.',
  },
  {
    id: 'bullseye',
    label: 'Bullseye',
    emoji: '🎪',
    description: 'Forecast the exact actual value.',
  },
  {
    id: 'contrarian',
    label: 'Contrarian',
    emoji: '🦄',
    description: 'Won going against the MNI consensus direction.',
  },
  {
    id: 'comeback-kid',
    label: 'Comeback Kid',
    emoji: '💪',
    description: 'Won right after 3 losses in a row.',
  },
  {
    id: 'fence-sitter',
    label: 'Fence Sitter',
    emoji: '🪑',
    description: 'Tied MNI three times.',
  },
  {
    id: 'iron-stomach',
    label: 'Iron Stomach',
    emoji: '🛡️',
    description: 'Survived a 5-loss losing streak without giving up.',
  },
];

export interface ResolutionContext {
  outcome: ForecastOutcome;
  exactMatch: boolean;
  contrarianWin: boolean;
  streakBeforeThisResolution: number;
}

function winRate(stats: ForecastGameStats): number {
  const total = stats.wins + stats.losses + stats.ties;
  return total > 0 ? stats.wins / total : 0;
}

/** Pure: given stats before/after folding in one resolved forecast, returns newly earned badge ids. */
export function evaluateNewBadges(
  prevStats: ForecastGameStats,
  nextStats: ForecastGameStats,
  resolution: ResolutionContext,
): string[] {
  const earned: string[] = [];
  const alreadyHas = (id: string) => prevStats.badges.includes(id) || earned.includes(id);
  const award = (id: string, condition: boolean) => {
    if (condition && !alreadyHas(id)) earned.push(id);
  };

  const wasFirstResolution = prevStats.wins + prevStats.losses + prevStats.ties === 0;
  const nextTotal = nextStats.wins + nextStats.losses + nextStats.ties;
  const specialistCount = computeSpecialists(nextStats.byIndicator).length;

  // Win Streak
  award('hot-streak-3', nextStats.currentStreak === 3);
  award('on-fire-5', nextStats.currentStreak === 5);
  award('iron-will-10', nextStats.currentStreak === 10);

  // Total Wins
  award('rookie-numbers', prevStats.wins < 5 && nextStats.wins >= 5);
  award('crystal-ball', prevStats.wins < 10 && nextStats.wins >= 10);
  award('half-century', prevStats.wins < 25 && nextStats.wins >= 25);

  // Accuracy (guarded by `alreadyHas`, so re-checking the threshold on every
  // resolution once earned is harmless — it just never re-fires)
  award('sharp-shooter', nextTotal >= 5 && winRate(nextStats) >= 0.55);
  award('marksman', nextTotal >= 10 && winRate(nextStats) >= 0.65);
  award('oracle', nextTotal >= 20 && winRate(nextStats) >= 0.75);

  // Specialists
  award('niche-expert', specialistCount >= 1);
  award('well-rounded', specialistCount >= 3);
  award('renaissance-forecaster', specialistCount >= 5);

  // Unique
  award('first-blood', wasFirstResolution);
  award('beat-the-machine', resolution.outcome === 'win' && prevStats.wins === 0);
  award('bullseye', resolution.exactMatch);
  award('contrarian', resolution.outcome === 'win' && resolution.contrarianWin);
  award(
    'comeback-kid',
    resolution.outcome === 'win' && resolution.streakBeforeThisResolution <= -3,
  );
  award('fence-sitter', nextStats.ties >= 3);
  award('iron-stomach', nextStats.currentStreak <= -5);

  return earned;
}
